import { Legend, Swatch } from "../bars";
import type { PageProps } from "../index";
import { Footnote, PageHead } from "../kit";
import { RowsPage, type RowsData } from "../rows";

export interface ReasonRow {
  funnel: string;
  item_id: string;
  item_name: string;
  drop_reason: string;
  options_n: number;
}

/** Bar order and colour: one hue family, so no funnel shouts over another. */
export const FUNNELS: readonly string[] = ["ingredients", "packaging", "services"];
export const FUNNEL_BAR: readonly string[] = ["bg-ks-purple", "bg-ks-purple-2", "bg-ks-grey-1"];
export const NO_REASON = "No reason recorded";

export interface ReasonLine {
  reason: string;
  /** Dropped options per funnel, in `FUNNELS` order. */
  counts: number[];
  total: number;
}

export interface ReasonModel {
  lines: ReasonLine[];
  /** The largest single bar, which the others are drawn against. */
  max: number;
  dropped: number;
  noReason: number;
}

/** One line per reason, most common first, with "No reason recorded" always last. */
export function reasonModel(rows: ReasonRow[]): ReasonModel {
  const byReason = new Map<string, ReasonLine>();
  for (const row of rows) {
    const index = FUNNELS.indexOf(row.funnel);
    if (index < 0) continue;
    let line = byReason.get(row.drop_reason);
    if (!line) {
      line = { reason: row.drop_reason, counts: FUNNELS.map(() => 0), total: 0 };
      byReason.set(row.drop_reason, line);
    }
    line.counts[index] += row.options_n;
    line.total += row.options_n;
  }
  const lines = Array.from(byReason.values()).sort(
    (a, b) => Number(a.reason === NO_REASON) - Number(b.reason === NO_REASON) || b.total - a.total || a.reason.localeCompare(b.reason),
  );
  return {
    lines,
    max: Math.max(1, ...lines.flatMap((line) => line.counts)),
    dropped: lines.reduce((sum, line) => sum + line.total, 0),
    noReason: byReason.get(NO_REASON)?.total ?? 0,
  };
}

export function noReasonNote(model: ReasonModel): string {
  const share = model.dropped > 0 ? Math.round((model.noReason / model.dropped) * 100) : 0;
  return `${model.noReason} of ${model.dropped} dropped options have no reason recorded (${share}%).`;
}

export function FunnelReasonBody({ data }: { data: RowsData<ReasonRow> }) {
  const model = reasonModel(data.rows);
  return (
    <>
      <Legend>
        {FUNNELS.map((funnel, index) => (
          <Swatch key={funnel} className={FUNNEL_BAR[index]}>
            {funnel}
          </Swatch>
        ))}
      </Legend>
      <div>
        {model.lines.map((line) => (
          <div key={line.reason} className="my-1.5 grid grid-cols-[240px_1fr] items-center gap-2.5 text-sm max-md:grid-cols-[140px_1fr]">
            <div>{line.reason === NO_REASON ? <b>{NO_REASON}</b> : line.reason}</div>
            <div className="flex flex-col gap-0.5" role="img" aria-label={`${line.reason}: ${FUNNELS.map((funnel, index) => `${funnel} ${line.counts[index]}`).join(", ")}`}>
              {line.counts.map((count, index) => (
                <div key={FUNNELS[index]} className="flex items-center gap-1.5">
                  <div className={`h-[9px] rounded-[2px] ${FUNNEL_BAR[index]}`} style={{ width: `${(count / model.max) * 85}%` }} />
                  <span className="text-xs">{count || ""}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <Footnote>{noReasonNote(model)}</Footnote>
    </>
  );
}

export function FunnelReasonPage({ tab, areaName }: PageProps) {
  return (
    <RowsPage<ReasonRow>
      tab={tab}
      areaName={areaName}
      head={(data) => (
        <PageHead
          title={tab.label}
          sub={data ? "Why options left the funnel" : undefined}
          kpi={data ? { label: "Dropped", value: reasonModel(data.rows).dropped.toLocaleString("en-GB") } : null}
        />
      )}
    >
      {(data) => <FunnelReasonBody data={data} />}
    </RowsPage>
  );
}
